import * as React from "react";
import { cn } from "@/lib/utils";
import { Inbox, LucideIcon } from "lucide-react";
import { Button } from "./button";

interface EmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
  icon?: React.ReactNode | LucideIcon;
  title: string;
  description?: string;
  action?: React.ReactNode;
  primaryAction?: {
    label: string;
    onClick: () => void;
    loading?: boolean;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  size?: "sm" | "md" | "lg";
}

const sizeClasses = {
  sm: {
    container: "py-6",
    icon: "h-8 w-8",
    iconWrapper: "h-14 w-14",
    title: "text-sm",
    description: "text-xs",
  },
  md: {
    container: "py-10",
    icon: "h-10 w-10",
    iconWrapper: "h-16 w-16",
    title: "text-base",
    description: "text-sm",
  },
  lg: {
    container: "py-16",
    icon: "h-12 w-12",
    iconWrapper: "h-20 w-20",
    title: "text-lg",
    description: "text-base",
  },
};

const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  (
    {
      icon,
      title,
      description,
      action,
      primaryAction,
      secondaryAction,
      size = "md",
      className,
      ...props
    },
    ref
  ) => {
    const sizeClass = sizeClasses[size];

    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-col items-center justify-center text-center",
          sizeClass.container,
          className
        )}
        {...props}
      >
        <div
          className={cn(
            "flex items-center justify-center rounded-full bg-muted mb-4",
            sizeClass.iconWrapper
          )}
        >
          {(() => {
            if (!icon) {
              return <Inbox className={cn("text-muted-foreground", sizeClass.icon)} />;
            }
            // Check if it's already a valid React element
            if (React.isValidElement(icon)) {
              return <div className={cn("text-muted-foreground", sizeClass.icon)}>{icon}</div>;
            }
            // It's a component type (function or forwardRef) - render it
            const IconComponent = icon as LucideIcon;
            return <IconComponent className={cn("text-muted-foreground", sizeClass.icon)} />;
          })()}
        </div>

        <h3
          className={cn(
            "font-semibold text-foreground",
            sizeClass.title
          )}
        >
          {title}
        </h3>

        {description && (
          <p
            className={cn(
              "text-muted-foreground mt-1 max-w-sm",
              sizeClass.description
            )}
          >
            {description}
          </p>
        )}

        {/* Custom action slot (legacy support) */}
        {action && <div className="mt-4">{action}</div>}

        {/* New action buttons */}
        {(primaryAction || secondaryAction) && (
          <div className="mt-6 flex flex-col sm:flex-row items-center gap-3">
            {primaryAction && (
              <Button
                onClick={primaryAction.onClick}
                loading={primaryAction.loading}
              >
                {primaryAction.label}
              </Button>
            )}
            {secondaryAction && (
              <Button
                variant="ghost"
                onClick={secondaryAction.onClick}
              >
                {secondaryAction.label}
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }
);
EmptyState.displayName = "EmptyState";

export { EmptyState };
export type { EmptyStateProps };
